import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { Provider, useSelector } from "react-redux";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { RootState } from "./store/store";
import { LoadingScreen } from "./components/LoadingScreen";
import { MainLayout } from "./components/layouts/MainLayout/MainLayout";
import { LoginPage } from "./pages/auth/LoginPage";
import { SignupPage } from "./pages/auth/SignupPage";
import { ForgotPasswordPage } from "./pages/auth/ForgotPasswordPage";
import { SandboxPage } from "./pages/SandboxPage";
import { DocumentsPage } from "./pages/DocumentsPage";
import { PropertiesPage } from "./pages/PropertiesPage";
import { PropertyDetailsPage } from "./pages/PropertyDetailsPage";
import { ProtectedRoute } from "./features/auth/components/ProtectedRoute";
import { PublicOnlyRoute } from "./features/auth/components/PublicOnlyRoute";
import { store } from "./store/store";
import { theme } from "./theme";
import { DocumentDetailsPage } from "./pages/DocumentDetailsPage";
import { NavigationListener } from "./features/analytics/components/NavigationListener";

const AppContent: React.FC = () => {
  const initialized = useSelector((state: RootState) => state.auth.initialized);

  if (!initialized) {
    return <LoadingScreen />;
  }

  return (
    <Router>
      <NavigationListener>
        <Routes>
          <Route
            path="/login"
            element={
              <PublicOnlyRoute>
                <LoginPage />
              </PublicOnlyRoute>
            }
          />
          <Route
            path="/signup"
            element={
              <PublicOnlyRoute>
                <SignupPage />
              </PublicOnlyRoute>
            }
          />
          <Route
            path="/forgot-password"
            element={
              <PublicOnlyRoute>
                <ForgotPasswordPage />
              </PublicOnlyRoute>
            }
          />
          <Route
            path="/documents"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <DocumentsPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          {process.env.NODE_ENV === "development" && (
            <Route
              path="/sandbox"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <SandboxPage />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
          )}
          <Route
            path="/documents/:id"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <DocumentDetailsPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/properties"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <PropertiesPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/properties/:id"
            element={
              <ProtectedRoute>
                <PropertyDetailsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/properties/:propertyId/documents/:documentId"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <DocumentDetailsPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route path="/" element={<Navigate to="/documents" replace />} />
        </Routes>
      </NavigationListener>
    </Router>
  );
};

const App: React.FC = () => {
  return (
    <Provider store={store}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AppContent />
      </ThemeProvider>
    </Provider>
  );
};

export default App;
